import { useState } from 'react';
import { useUserPageContextValueHook } from '@/contexts/UserPageContextProvider';
import { FONTS, PAGE_FONTS, isProFont, useFonts } from '@/views/mypage/components/FdDesignSetting/premiumDesign';
import { ProBadge, useGoPlanTab, usePagePro } from '@/views/mypage/components/FdSubscription/proPlan';

// 텍스트 모듈 꾸미기 값. 서버 LinkItemsMainService(TEXT_*), 공개 페이지 v2/components/textStyles.js 와 같아야 한다
export const TEXT_ALIGNS = [
    { value: 'LEFT', label: '왼쪽', className: 'text-left' },
    { value: 'CENTER', label: '가운데', className: 'text-center' },
    { value: 'RIGHT', label: '오른쪽', className: 'text-right' },
];

// 폰트 목록은 FdDesignSetting/premiumDesign.js (기본 외에는 Pro). 기본 = 페이지 폰트를 따름
export const TEXT_FONTS = [{ value: 'DEFAULT', label: '페이지 폰트', family: null }, ...FONTS];

export const TEXT_SIZES = [
    { value: 'SMALL', label: '작게', px: 12 },
    { value: 'MEDIUM', label: '보통', px: 14 },
    { value: 'LARGE', label: '크게', px: 18 },
];

const COLOR_SWATCHES = ['#111111', '#6B7280', '#FFFFFF', '#EE9CA7', '#EF4444', '#F59E0B', '#10B981', '#3B82F6', '#8B5CF6'];

/** 저장된 textConfig 에 기본값을 채운다 (예전 모듈: 왼쪽 · 기본 폰트 · 보통 · 페이지 기본 색) */
export function resolveTextStyle(textConfig) {
    return {
        align: textConfig?.align || 'LEFT',
        font: textConfig?.font || 'DEFAULT',
        size: textConfig?.size || 'MEDIUM',
        color: textConfig?.color || null,
    };
}

export const useTextModuleFonts = useFonts;

/** 페이지 폰트의 font-family (모듈 폰트가 기본일 때 미리보기에 쓴다) */
export const pageFontFamily = (userPage) => PAGE_FONTS.find(f => f.value === userPage?.pageFont)?.family || null;

/** 글자에 꾸미기를 적용할 style (미리보기·목록 카드 공용). pageFamily: 기본 폰트일 때 따를 페이지 폰트 */
export function textStyleProps(style, defaultColor, pageFamily = null) {
    const family = TEXT_FONTS.find(f => f.value === style.font)?.family || pageFamily;
    return {
        className: `${TEXT_ALIGNS.find(a => a.value === style.align)?.className} ${family ? 'font-normal' : 'font-semibold'}`,
        style: {
            fontSize: TEXT_SIZES.find(s => s.value === style.size)?.px,
            color: style.color || defaultColor,
            ...(family ? { fontFamily: family } : {}),
        },
    };
}

const SEGMENT = (active) => `flex-1 rounded-lg px-2 py-2 text-[13px] font-semibold ring-1 transition ${active ? 'bg-gray-900 text-white ring-gray-900' : 'bg-white text-gray-700 ring-gray-200 hover:bg-gray-50'}`;

/**
 * 텍스트 꾸미기 입력 (정렬·크기·폰트·색) + 페이지 배경 위 미리보기.
 * 텍스트 모듈은 전부, 캐러셀·앨범 설명은 크기·미리보기 없이 '테마 기본' 폰트·색을 더해 쓴다.
 */
export function TextStyleFields({ text, style, onChange, showSize = true, showPreview = true, fonts = TEXT_FONTS, defaultColorLabel = '페이지 기본' }) {
    useFonts();
    const userPage = useUserPageContextValueHook()?.userPage;
    const isPro = usePagePro();
    const goPlan = useGoPlanTab();
    const pageColor = userPage?.domainNameColor || '#111111';
    const preview = textStyleProps(style, pageColor, pageFontFamily(userPage));
    // Pro 가 아니면 Pro 폰트는 못 고른다 (이미 골라 둔 폰트는 그대로 둘 수 있음 — 서버도 같은 규칙)
    const [initialFont] = useState(style.font);

    return (
        <div className="flex flex-col gap-4">
            {showPreview && <div
                className="rounded-xl px-4 py-3 ring-1 ring-black/5"
                style={{ background: userPage?.backgroundType === 'IMAGE' ? '#444' : (userPage?.backgroundColorHex || '#f7f7f5') }}
                aria-label="미리보기"
            >
                <p className={`whitespace-pre-line break-words ${preview.className}`} style={preview.style}>{text || '텍스트를 입력하면 여기에 보여요'}</p>
            </div>}

            <div className="flex flex-col gap-1.5">
                <span className="text-sm font-bold">정렬</span>
                <div className="flex gap-2">
                    {TEXT_ALIGNS.map(a => <button key={a.value} type="button" aria-pressed={style.align === a.value} onClick={() => onChange({ align: a.value })} className={SEGMENT(style.align === a.value)}>{a.label}</button>)}
                </div>
            </div>

            {showSize && <div className="flex flex-col gap-1.5">
                <span className="text-sm font-bold">크기</span>
                <div className="flex gap-2">
                    {TEXT_SIZES.map(s => <button key={s.value} type="button" aria-pressed={style.size === s.value} onClick={() => onChange({ size: s.value })} className={SEGMENT(style.size === s.value)}>{s.label}</button>)}
                </div>
            </div>}

            <div className="flex flex-col gap-1.5">
                <span className="flex items-center gap-1.5 text-sm font-bold">폰트 <ProBadge /></span>
                {!isPro && <p className="text-xs text-gray-500 break-keep">폰트 꾸미기는 슈룩 Pro 에서 쓸 수 있어요. <button type="button" onClick={goPlan} className="font-bold text-gray-900 underline">Pro 알아보기</button></p>}
                <div className="grid grid-cols-3 gap-2">
                    {fonts.map(f => {
                        const locked = !isPro && isProFont(f.value) && f.value !== initialFont;
                        return (
                            <button key={f.value ?? 'THEME'} type="button" disabled={locked} aria-pressed={style.font === f.value} onClick={() => onChange({ font: f.value })} className={`${SEGMENT(style.font === f.value)} ${locked ? 'cursor-not-allowed opacity-40' : ''}`} style={f.family ? { fontFamily: f.family, fontWeight: 400 } : undefined}>
                                {f.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className="flex flex-col gap-1.5">
                <span className="text-sm font-bold">색상</span>
                <div className="flex flex-wrap items-center gap-2">
                    <button type="button" aria-pressed={!style.color} onClick={() => onChange({ color: null })} className={`rounded-full px-3 py-1.5 text-[12px] font-semibold ring-1 ${!style.color ? 'bg-gray-900 text-white ring-gray-900' : 'bg-white text-gray-700 ring-gray-200'}`}>
                        {defaultColorLabel}
                    </button>
                    {COLOR_SWATCHES.map(c => (
                        <button
                            key={c}
                            type="button"
                            onClick={() => onChange({ color: c })}
                            aria-label={`색상 ${c}`}
                            aria-pressed={style.color?.toUpperCase() === c}
                            className={`h-7 w-7 rounded-full ring-1 ring-black/10 ${style.color?.toUpperCase() === c ? 'outline outline-2 outline-offset-2 outline-gray-900' : ''}`}
                            style={{ background: c }}
                        />
                    ))}
                    <label className="relative flex h-7 w-7 cursor-pointer items-center justify-center overflow-hidden rounded-full bg-[conic-gradient(red,yellow,lime,cyan,blue,magenta,red)] ring-1 ring-black/10" title="직접 고르기">
                        <input type="color" value={style.color || pageColor} onChange={(e) => onChange({ color: e.target.value.toUpperCase() })} className="absolute inset-0 cursor-pointer opacity-0" aria-label="색상 직접 고르기" />
                    </label>
                </div>
            </div>
        </div>
    );
}
